import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CustomDatePicker from '../../components/CustomDatePicker';
import {
    Landmark, Wallet, XCircle, Scissors, Percent as PercentIcon,
    Loader2, ChevronDown, RotateCcw, Send, Navigation, Plus, Trash2,
} from 'lucide-react';

import {
    fetchFDPaymentTypes, fetchFDBankList, fetchFDNumbers, fetchFDDetailsByFdno,
    fetchFDLastTransaction, fetchReturnBudgetFYYears,
    fetchFDDCAList, fetchFDSubDCAByDCACode, fetchFDIntDeductionDefault,
    submitOpenFD, submitPartialFD, submitFDInterest,
    clearFDDetails, clearFDSaveResult, resetFDPayments,
    selectFDPayments,
} from '../../slices/accountsSlice/fdPaymentsSlice';

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** CustomDatePicker's onChange hands back a Date object */
const toDDMonYYYY = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const joinIds = (arr) => (arr && arr.length ? arr.join(',') + ',' : '');

const MODE_OPTIONS = [{ Mode: 'RTGS/E-Trans', Mode_Desc: 'RTGS/E-Trans' }];

// ── Shared UI primitives (matches ClientInvoiceCreation) ──────────────────────
const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-indigo-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

const YesNoToggle = ({ value, onChange, disabled }) => (
    <div className="flex gap-3 mt-1">
        {['Yes', 'No'].map((opt) => (
            <button
                key={opt} type="button" disabled={disabled}
                onClick={() => onChange(opt)}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold border-2 transition-all disabled:opacity-60 ${
                    value === opt
                        ? opt === 'Yes' ? 'bg-green-500 border-green-500 text-white shadow-sm' : 'bg-rose-500 border-rose-500 text-white shadow-sm'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                }`}
            >
                {opt}
            </button>
        ))}
    </div>
);

const Card = ({ children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">{children}</div>
);

// ═════════════════════════════════════════════════════════════════════════════
// OPEN FD
// ═════════════════════════════════════════════════════════════════════════════

const BLANK_OPEN = { FDRNo: '', FromDate: '', ToDate: '', ROI: '', Amount: '', BankId: '', ModeOfPay: MODE_OPTIONS[0].Mode, PaymentNo: '', PaymentDate: '', Remarks: '' };

const OpenFDSection = ({ currentUser, roleId, bankList, bankListLoading }) => {
    const dispatch = useDispatch();
    const { saveLoading, saveResult, saveError } = useSelector(selectFDPayments);
    const [form, setForm] = useState(BLANK_OPEN);

    useEffect(() => {
        if (saveResult) { toast.success('Fixed Deposit opened successfully.'); dispatch(clearFDSaveResult()); setForm(BLANK_OPEN); }
        if (saveError) { toast.error(saveError); dispatch(clearFDSaveResult()); }
    }, [saveResult, saveError, dispatch]);

    const set = (key) => (val) => setForm((p) => ({ ...p, [key]: val }));
    const paymentAmount = parseFloat(form.Amount) || 0;

    const handleReset = () => setForm(BLANK_OPEN);

    const handleSubmit = () => {
        if (!form.FDRNo.trim()) { toast.warning('Enter the FD Number.'); return; }
        if (!form.FromDate) { toast.warning('Select the From Date.'); return; }
        if (!form.ToDate) { toast.warning('Select the To Date.'); return; }
        if (!form.ROI || parseFloat(form.ROI) <= 0) { toast.warning('Enter a valid Rate of Interest.'); return; }
        if (!form.Amount || parseFloat(form.Amount) <= 0) { toast.warning('Enter a valid FD Amount.'); return; }
        if (!form.BankId) { toast.warning('Select a Payment Bank.'); return; }
        if (!form.ModeOfPay) { toast.warning('Select a Mode of Pay.'); return; }
        if (!form.PaymentNo.trim()) { toast.warning('Enter the Payment Number.'); return; }
        if (!form.PaymentDate) { toast.warning('Select the Payment Date.'); return; }
        if (!form.Remarks.trim()) { toast.warning('Enter Remarks.'); return; }

        dispatch(submitOpenFD({
            FDRNo: form.FDRNo.trim(),
            FDRFromDate: toDDMonYYYY(form.FromDate),
            FDRClosingDate: toDDMonYYYY(form.ToDate),
            FDRAmount: parseFloat(form.Amount),
            FDRROI: parseFloat(form.ROI),
            Createdby: currentUser,
            Bankid: form.BankId,
            Remarks: form.Remarks,
            ModeofPay: form.ModeOfPay,
            No: form.PaymentNo,
            PaymentDate: toDDMonYYYY(form.PaymentDate),
            Roleid: roleId,
            Action: 'New',
        }));
    };

    return (
        <>
            <Card>
                <CardHeader icon={Landmark} title="Open a New Fixed Deposit" subtitle="Record a newly opened FD"
                    action={<button onClick={handleReset} disabled={saveLoading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> Reset</button>} />
                <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Label required>FD Number</Label>
                        <input className={inputCls} value={form.FDRNo} onChange={(e) => set('FDRNo')(e.target.value)} disabled={saveLoading} placeholder="Enter FD number" />
                    </div>
                    <div>
                        <Label required>From Date</Label>
                        <CustomDatePicker value={form.FromDate} onChange={set('FromDate')} disabled={saveLoading} maxDate={new Date()} />
                    </div>
                    <div>
                        <Label required>To Date</Label>
                        <CustomDatePicker value={form.ToDate} onChange={set('ToDate')} disabled={saveLoading} />
                    </div>
                    <div>
                        <Label required>Rate of Interest (%)</Label>
                        <input className={inputCls} value={form.ROI} onChange={(e) => set('ROI')(e.target.value)} disabled={saveLoading} inputMode="decimal" />
                    </div>
                    <div>
                        <Label required>FD Amount</Label>
                        <input className={inputCls} value={form.Amount} onChange={(e) => set('Amount')(e.target.value)} disabled={saveLoading} inputMode="decimal" />
                    </div>
                    <div>
                        <Label>Payment Amount</Label>
                        <input className={inputCls} value={paymentAmount ? paymentAmount.toFixed(2) : ''} disabled readOnly />
                    </div>
                </div>
            </Card>

            <Card>
                <CardHeader icon={Wallet} title="Payment Details" subtitle="How and when this FD was paid" />
                <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Label required>Payment Bank</Label>
                        <div className="relative">
                            <select className={`${inputCls} appearance-none pr-10`} value={form.BankId} onChange={(e) => set('BankId')(e.target.value)} disabled={saveLoading || bankListLoading}>
                                <option value="">— Select Bank —</option>
                                {bankList.map((b) => <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>)}
                            </select>
                            <SelectIcon loading={bankListLoading} />
                        </div>
                    </div>
                    <div>
                        <Label required>Mode of Pay</Label>
                        <div className="relative">
                            <select className={`${inputCls} appearance-none pr-10`} value={form.ModeOfPay} onChange={(e) => set('ModeOfPay')(e.target.value)} disabled={saveLoading}>
                                {MODE_OPTIONS.map((m) => <option key={m.Mode} value={m.Mode}>{m.Mode_Desc}</option>)}
                            </select>
                            <SelectIcon loading={false} />
                        </div>
                    </div>
                    <div>
                        <Label required>Payment Number</Label>
                        <input className={inputCls} value={form.PaymentNo} onChange={(e) => set('PaymentNo')(e.target.value)} disabled={saveLoading} />
                    </div>
                    <div>
                        <Label required>Payment Date</Label>
                        <CustomDatePicker value={form.PaymentDate} onChange={set('PaymentDate')} disabled={saveLoading} maxDate={new Date()} />
                    </div>
                    <div className="md:col-span-2">
                        <Label required>Remarks</Label>
                        <textarea rows={2} className={`${inputCls} resize-none`} value={form.Remarks} onChange={(e) => set('Remarks')(e.target.value)} disabled={saveLoading} />
                    </div>
                </div>
            </Card>

            <div className="flex items-center justify-end gap-3 pb-2">
                <button onClick={handleReset} disabled={saveLoading} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                    <RotateCcw className="h-4 w-4" /> Reset
                </button>
                <button onClick={handleSubmit} disabled={saveLoading} className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                    {saveLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><Send className="h-4 w-4" /> Submit</>}
                </button>
            </div>
        </>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// CLOSE / PARTIALLY CLOSE FD (shared form)
// ═════════════════════════════════════════════════════════════════════════════

const BLANK_ROW = () => ({ dca: '', subDca: '', amount: '', confirmed: false });

const ClosePartialFDSection = ({ mode, currentUser, roleId, fdNumbers, fdNumbersLoading, dcaList, dcaListLoading, subDcaByDca, subDcaLoadingByDca, onLoadSubDca }) => {
    const dispatch = useDispatch();
    const { fdDetails, fdDetailsLoading, lastTransaction, returnBudgetFYYears, returnBudgetFYYearsLoading, saveLoading, saveResult, saveError } = useSelector(selectFDPayments);

    const [fdId, setFdId] = useState('');
    const [closingDate, setClosingDate] = useState('');
    const [maturityAmount, setMaturityAmount] = useState('');
    const [interest, setInterest] = useState('');
    const [returnBudgetFYYear, setReturnBudgetFYYear] = useState('');
    const [deductionEnabled, setDeductionEnabled] = useState(false);
    const [rows, setRows] = useState([]);
    const [modeOfPay, setModeOfPay] = useState(MODE_OPTIONS[0].Mode);
    const [paymentNo, setPaymentNo] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [remarks, setRemarks] = useState('');

    const fdNoText = fdNumbers.find((f) => String(f.FdID) === String(fdId))?.FdNo || '';
    // GetFDDetailsbyFdno never returns bank info — the bank is read from the FD's most recent
    // approved transaction instead (open / prior partial / prior close), matching legacy's
    // GetLastTransction() → GetFDLastTransactionDetails → PaymentBankName.
    const bankName = lastTransaction?.PaymentBankName || '';
    const balance = parseFloat(fdDetails?.FDRBalanceAmount) || 0;

    const resetForm = () => {
        setClosingDate(''); setMaturityAmount(''); setInterest(''); setReturnBudgetFYYear('');
        setDeductionEnabled(false); setRows([]);
        setModeOfPay(MODE_OPTIONS[0].Mode); setPaymentNo(''); setPaymentDate(''); setRemarks('');
    };

    const handleFdChange = (val) => {
        setFdId(val);
        resetForm();
        dispatch(clearFDDetails());
        const label = fdNumbers.find((f) => String(f.FdID) === String(val))?.FdNo;
        if (label) {
            dispatch(fetchFDDetailsByFdno(label));
            dispatch(fetchFDLastTransaction(label));
            dispatch(fetchReturnBudgetFYYears(label));
        }
    };

    useEffect(() => {
        if (mode === 'Close' && fdDetails) setMaturityAmount(String(fdDetails.FDRBalanceAmount ?? ''));
    }, [mode, fdDetails]);

    useEffect(() => {
        if (saveResult) {
            toast.success(mode === 'Close' ? 'FD closed successfully.' : 'Partial FD closure saved successfully.');
            dispatch(clearFDSaveResult());
            setFdId(''); resetForm(); dispatch(clearFDDetails());
        }
        if (saveError) { toast.error(saveError); dispatch(clearFDSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError]);

    const toggleDeduction = (val) => {
        setDeductionEnabled(val === 'Yes');
        if (val === 'Yes' && rows.length === 0) setRows([BLANK_ROW()]);
        if (val === 'No') setRows([]);
    };

    const addRow = () => setRows((p) => [...p, BLANK_ROW()]);
    const removeRow = (idx) => setRows((p) => { const next = p.filter((_, i) => i !== idx); if (next.length === 0) setDeductionEnabled(false); return next; });
    const updateRow = (idx, patch) => setRows((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const handleRowDCAChange = (idx, code) => {
        updateRow(idx, { dca: code, subDca: '' });
        if (code && !subDcaByDca[code]) onLoadSubDca(code);
    };

    const deductionsTotal = useMemo(() => rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0), [rows]);
    const paymentAmount = (parseFloat(maturityAmount) || 0) + (parseFloat(interest) || 0) - deductionsTotal;

    const handleReset = () => { setFdId(''); resetForm(); dispatch(clearFDDetails()); };

    const handleSubmit = () => {
        if (!fdId) { toast.warning('Select an FD Number.'); return; }
        if (!closingDate) { toast.warning('Select the Closing Date.'); return; }
        if (!maturityAmount || parseFloat(maturityAmount) === 0) { toast.warning('Enter a valid Maturity Amount.'); return; }
        const maturity = parseFloat(maturityAmount);
        if (balance < maturity) { toast.warning('Invalid Maturity Amount — exceeds FD balance.'); return; }
        if (mode === 'Partial' && balance <= maturity) { toast.warning('Maturity Amount must be less than the FD balance for a partial closure.'); return; }
        if (mode === 'Close' && balance !== maturity) { toast.warning('Maturity Amount must exactly equal the FD balance to close it.'); return; }
        if (!interest.trim()) { toast.warning('Enter the Interest amount.'); return; }
        if (!returnBudgetFYYear) { toast.warning('Select the Budget Return FY Year.'); return; }
        if (!modeOfPay) { toast.warning('Select a Mode of Pay.'); return; }
        if (!paymentNo.trim()) { toast.warning('Enter the Payment Number.'); return; }
        if (!paymentDate) { toast.warning('Select the Payment Date.'); return; }
        if (!remarks.trim()) { toast.warning('Enter Remarks.'); return; }
        if (paymentAmount < 0) { toast.warning('Computed Payment Amount is invalid.'); return; }

        if (deductionEnabled) {
            for (const r of rows) {
                if (!r.dca || !r.subDca || !r.amount || parseFloat(r.amount) === 0) { toast.warning('Complete every deduction row (Account Head, Sub Account Head and Amount).'); return; }
                if (!r.confirmed) { toast.warning('Confirm every deduction row using its checkbox.'); return; }
            }
            const combos = rows.map((r) => `${r.dca},${r.subDca}`);
            if (new Set(combos).size !== combos.length) { toast.warning('Duplicate Account Head for deduction — remove the duplicate row.'); return; }
        }

        dispatch(submitPartialFD({
            FDRNo: fdNoText,
            Createdby: currentUser,
            BankName: bankName,
            Remarks: remarks,
            PaymentModeofPay: modeOfPay,
            PaymentNo: paymentNo,
            PaymentDate: toDDMonYYYY(paymentDate),
            DedDcas: deductionEnabled ? joinIds(rows.map((r) => r.dca)) : '',
            DedSDcas: deductionEnabled ? joinIds(rows.map((r) => r.subDca)) : '',
            DedAmounts: deductionEnabled ? joinIds(rows.map((r) => r.amount)) : '',
            FDRClosingDate: toDDMonYYYY(closingDate),
            FDRAmount: maturity,
            FDRROI: parseFloat(interest),
            Status: '1',
            PaymenttypeName: mode === 'Close' ? 'FDClose' : 'FDPartial',
            Fdtype: mode,
            Roleid: roleId,
            PaymentAmount: paymentAmount,
            ReturnBudgetFYYear: returnBudgetFYYear,
        }));
    };

    return (
        <>
            <Card>
                <CardHeader icon={mode === 'Close' ? XCircle : Scissors} title={mode === 'Close' ? 'Close Fixed Deposit' : 'Partially Close Fixed Deposit'} subtitle="Select the FD to close or partially withdraw from"
                    action={<button onClick={handleReset} disabled={saveLoading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> Reset</button>} />
                <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Label required>FD Number</Label>
                        <div className="relative">
                            <select className={`${inputCls} appearance-none pr-10`} value={fdId} onChange={(e) => handleFdChange(e.target.value)} disabled={saveLoading || fdNumbersLoading}>
                                <option value="">— Select FD —</option>
                                {fdNumbers.map((f) => <option key={f.FdID} value={f.FdID}>{f.FdNo}</option>)}
                            </select>
                            <SelectIcon loading={fdNumbersLoading} />
                        </div>
                    </div>
                    {fdId && (
                        fdDetailsLoading ? (
                            <div className="md:col-span-2 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" />Loading FD details…</div>
                        ) : fdDetails ? (
                            <>
                                <div><Label>Begin Date</Label><input className={inputCls} value={fdDetails.FDRFromDate || ''} disabled readOnly /></div>
                                <div><Label>End Date</Label><input className={inputCls} value={fdDetails.FDRToDate || ''} disabled readOnly /></div>
                                <div><Label>Original Amount</Label><input className={inputCls} value={fdDetails.FDRAmount ?? ''} disabled readOnly /></div>
                                <div><Label>Rate of Interest</Label><input className={inputCls} value={fdDetails.FDRROI ?? ''} disabled readOnly /></div>
                                <div><Label>Balance</Label><input className={inputCls} value={fdDetails.FDRBalanceAmount ?? ''} disabled readOnly /></div>
                            </>
                        ) : null
                    )}
                </div>
            </Card>

            {fdId && (
                <>
                    <Card>
                        <CardHeader icon={mode === 'Close' ? XCircle : Scissors} title="Closure Details" />
                        <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Label required>Closing Date</Label>
                                <CustomDatePicker value={closingDate} onChange={setClosingDate} disabled={saveLoading} maxDate={new Date()} />
                            </div>
                            <div>
                                <Label required>Maturity Amount</Label>
                                <input className={inputCls} value={maturityAmount} onChange={(e) => setMaturityAmount(e.target.value)} disabled={saveLoading || mode === 'Close'} inputMode="decimal" />
                            </div>
                            <div>
                                <Label required>Interest</Label>
                                <input className={inputCls} value={interest} onChange={(e) => setInterest(e.target.value)} disabled={saveLoading} inputMode="decimal" />
                            </div>
                            <div>
                                <Label required>Budget Return FY Year</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={returnBudgetFYYear} onChange={(e) => setReturnBudgetFYYear(e.target.value)} disabled={saveLoading || returnBudgetFYYearsLoading}>
                                        <option value="">— Select —</option>
                                        {returnBudgetFYYears.map((y) => <option key={y.Year} value={y.Year}>{y.YearValue}</option>)}
                                    </select>
                                    <SelectIcon loading={returnBudgetFYYearsLoading} />
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <CardHeader icon={PercentIcon} title="Deductions" subtitle="Optional account-head deductions from the maturity payout" />
                        <div className="p-6 md:p-8 space-y-4">
                            <div className="max-w-xs">
                                <Label>Any Deductions?</Label>
                                <YesNoToggle value={deductionEnabled ? 'Yes' : 'No'} onChange={toggleDeduction} disabled={saveLoading || !closingDate} />
                                {!closingDate && <p className="text-xs text-rose-500 mt-1">Select a Closing Date first.</p>}
                            </div>

                            {deductionEnabled && (
                                <div className="space-y-3">
                                    {rows.map((row, idx) => (
                                        <div key={idx} className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                                            <div>
                                                <Label required>Account Head</Label>
                                                <div className="relative">
                                                    <select className={`${inputCls} appearance-none pr-10`} value={row.dca} onChange={(e) => handleRowDCAChange(idx, e.target.value)} disabled={saveLoading || dcaListLoading}>
                                                        <option value="">— Select —</option>
                                                        {dcaList.map((d) => <option key={d.DCAID} value={d.DCACode}>{d.DCAIDSTR}</option>)}
                                                    </select>
                                                    <SelectIcon loading={dcaListLoading} />
                                                </div>
                                            </div>
                                            <div>
                                                <Label required>Sub Account Head</Label>
                                                <div className="relative">
                                                    <select className={`${inputCls} appearance-none pr-10`} value={row.subDca} onChange={(e) => updateRow(idx, { subDca: e.target.value })} disabled={saveLoading || !row.dca}>
                                                        <option value="">— Select —</option>
                                                        {(subDcaByDca[row.dca] || []).map((s) => <option key={s.SubDCAID} value={s.SubDCACode}>{s.SubDCAIDSTR}</option>)}
                                                    </select>
                                                    <SelectIcon loading={subDcaLoadingByDca[row.dca]} />
                                                </div>
                                            </div>
                                            <div>
                                                <Label required>Amount</Label>
                                                <input className={inputCls} value={row.amount} onChange={(e) => updateRow(idx, { amount: e.target.value })} disabled={saveLoading} inputMode="decimal" />
                                            </div>
                                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                                                <input type="checkbox" checked={row.confirmed} onChange={(e) => updateRow(idx, { confirmed: e.target.checked })} disabled={saveLoading} />
                                                Confirm
                                            </label>
                                            <button onClick={() => removeRow(idx)} disabled={saveLoading} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 justify-self-start">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <button onClick={addRow} disabled={saveLoading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors">
                                        <Plus className="h-3.5 w-3.5" /> Add More Deduction
                                    </button>
                                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">Total Deductions: {deductionsTotal.toFixed(2)}</div>
                                </div>
                            )}
                        </div>
                    </Card>

                    <Card>
                        <CardHeader icon={Wallet} title="Payment Details" />
                        <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div><Label>Bank</Label><input className={inputCls} value={bankName} disabled readOnly /></div>
                            <div>
                                <Label required>Mode of Pay</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={modeOfPay} onChange={(e) => setModeOfPay(e.target.value)} disabled={saveLoading}>
                                        {MODE_OPTIONS.map((m) => <option key={m.Mode} value={m.Mode}>{m.Mode_Desc}</option>)}
                                    </select>
                                    <SelectIcon loading={false} />
                                </div>
                            </div>
                            <div><Label required>Payment Number</Label><input className={inputCls} value={paymentNo} onChange={(e) => setPaymentNo(e.target.value)} disabled={saveLoading} /></div>
                            <div><Label required>Payment Date</Label><CustomDatePicker value={paymentDate} onChange={setPaymentDate} disabled={saveLoading} minDate={closingDate || undefined} maxDate={new Date()} /></div>
                            <div><Label>Payment Amount</Label><input className={inputCls} value={paymentAmount.toFixed(2)} disabled readOnly /></div>
                            <div className="md:col-span-3">
                                <Label required>Remarks</Label>
                                <textarea rows={2} className={`${inputCls} resize-none`} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={saveLoading} />
                            </div>
                        </div>
                    </Card>

                    <div className="flex items-center justify-end gap-3 pb-2">
                        <button onClick={handleReset} disabled={saveLoading} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                            <RotateCcw className="h-4 w-4" /> Reset
                        </button>
                        <button onClick={handleSubmit} disabled={saveLoading} className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                            {saveLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><Send className="h-4 w-4" /> Submit</>}
                        </button>
                    </div>
                </>
            )}
        </>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// INTEREST
// ═════════════════════════════════════════════════════════════════════════════

const InterestSection = ({ currentUser, roleId, fdNumbers, fdNumbersLoading, bankList, bankListLoading }) => {
    const dispatch = useDispatch();
    const { intDeductionDefault, intDeductionDefaultLoading, saveLoading, saveResult, saveError } = useSelector(selectFDPayments);

    const [fdId, setFdId] = useState('');
    const [intDate, setIntDate] = useState('');
    const [intAmount, setIntAmount] = useState('');
    const [deductionEnabled, setDeductionEnabled] = useState(false);
    const [dedAmount, setDedAmount] = useState('');
    const [bankId, setBankId] = useState('');
    const [modeOfPay, setModeOfPay] = useState(MODE_OPTIONS[0].Mode);
    const [paymentNo, setPaymentNo] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [remarks, setRemarks] = useState('');

    const fdNoText = fdNumbers.find((f) => String(f.FdID) === String(fdId))?.FdNo || '';
    const paymentAmount = (parseFloat(intAmount) || 0) - (deductionEnabled ? (parseFloat(dedAmount) || 0) : 0);

    useEffect(() => {
        if (saveResult) {
            toast.success('FD interest recorded successfully.');
            dispatch(clearFDSaveResult());
            handleReset();
        }
        if (saveError) { toast.error(saveError); dispatch(clearFDSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError]);

    const toggleDeduction = (val) => {
        const enabled = val === 'Yes';
        setDeductionEnabled(enabled);
        if (enabled) dispatch(fetchFDIntDeductionDefault());
        else setDedAmount('');
    };

    const handleReset = () => {
        setFdId(''); setIntDate(''); setIntAmount(''); setDeductionEnabled(false); setDedAmount('');
        setBankId(''); setModeOfPay(MODE_OPTIONS[0].Mode); setPaymentNo(''); setPaymentDate(''); setRemarks('');
    };

    const handleSubmit = () => {
        if (!fdId) { toast.warning('Select an FD Number.'); return; }
        if (!intDate) { toast.warning('Select the Interest Date.'); return; }
        if (!intAmount || parseFloat(intAmount) === 0) { toast.warning('Enter a valid Interest Amount.'); return; }
        if (deductionEnabled && (!dedAmount || parseFloat(dedAmount) === 0)) { toast.warning('Enter a valid Deduction Amount.'); return; }
        if (!bankId) { toast.warning('Select a Bank.'); return; }
        if (!modeOfPay) { toast.warning('Select a Mode of Pay.'); return; }
        if (!paymentNo.trim()) { toast.warning('Enter the Payment Number.'); return; }
        if (!paymentDate) { toast.warning('Select the Payment Date.'); return; }
        if (!remarks.trim()) { toast.warning('Enter Remarks.'); return; }
        if (paymentAmount <= 0) { toast.warning('Computed Payment Amount must be greater than zero.'); return; }

        dispatch(submitFDInterest({
            FDRNo: fdNoText,
            IntDate: toDDMonYYYY(intDate),
            IntAmount: parseFloat(intAmount),
            Capitalcc: deductionEnabled ? (intDeductionDefault?.CC || '') : '',
            DedDcas: deductionEnabled ? (intDeductionDefault?.DCA || '') : '',
            DedSDcas: deductionEnabled ? (intDeductionDefault?.SubDCA || '') : '',
            DedAmounts: deductionEnabled ? dedAmount : '',
            Bankid: bankId,
            Remarks: remarks,
            PaymentModeofPay: modeOfPay,
            PaymentNo: paymentNo,
            PaymentDate: toDDMonYYYY(paymentDate),
            Createdby: currentUser,
            Status: 1,
            PaymenttypeName: 'FDInterest',
            Roleid: roleId,
            Action: 'New',
            PaymentAmount: paymentAmount,
        }));
    };

    return (
        <>
            <Card>
                <CardHeader icon={PercentIcon} title="FD Interest" subtitle="Record interest earned on an existing FD"
                    action={<button onClick={handleReset} disabled={saveLoading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> Reset</button>} />
                <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Label required>FD Number</Label>
                        <div className="relative">
                            <select className={`${inputCls} appearance-none pr-10`} value={fdId} onChange={(e) => setFdId(e.target.value)} disabled={saveLoading || fdNumbersLoading}>
                                <option value="">— Select FD —</option>
                                {fdNumbers.map((f) => <option key={f.FdID} value={f.FdID}>{f.FdNo}</option>)}
                            </select>
                            <SelectIcon loading={fdNumbersLoading} />
                        </div>
                    </div>
                    <div>
                        <Label required>Interest Date</Label>
                        <CustomDatePicker value={intDate} onChange={setIntDate} disabled={saveLoading} maxDate={new Date()} />
                    </div>
                    <div>
                        <Label required>Interest Amount</Label>
                        <input className={inputCls} value={intAmount} onChange={(e) => setIntAmount(e.target.value)} disabled={saveLoading} inputMode="decimal" />
                    </div>
                </div>
            </Card>

            <Card>
                <CardHeader icon={PercentIcon} title="Deduction" subtitle="Optional — uses the system default TDS account head" />
                <div className="p-6 md:p-8 space-y-4">
                    <div className="max-w-xs">
                        <Label>Any Deduction?</Label>
                        <YesNoToggle value={deductionEnabled ? 'Yes' : 'No'} onChange={toggleDeduction} disabled={saveLoading} />
                    </div>
                    {deductionEnabled && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div><Label>Cost Centre</Label><input className={inputCls} value={intDeductionDefaultLoading ? 'Loading…' : (intDeductionDefault?.CC || '')} disabled readOnly /></div>
                            <div><Label>Account Head</Label><input className={inputCls} value={intDeductionDefaultLoading ? 'Loading…' : (intDeductionDefault?.DCA || '')} disabled readOnly /></div>
                            <div><Label>Sub Account Head</Label><input className={inputCls} value={intDeductionDefaultLoading ? 'Loading…' : (intDeductionDefault?.SubDCA || '')} disabled readOnly /></div>
                            <div>
                                <Label required>Deduction Amount</Label>
                                <input className={inputCls} value={dedAmount} onChange={(e) => setDedAmount(e.target.value)} disabled={saveLoading} inputMode="decimal" />
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            <Card>
                <CardHeader icon={Wallet} title="Payment Details" />
                <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Label required>Bank</Label>
                        <div className="relative">
                            <select className={`${inputCls} appearance-none pr-10`} value={bankId} onChange={(e) => setBankId(e.target.value)} disabled={saveLoading || bankListLoading}>
                                <option value="">— Select Bank —</option>
                                {bankList.map((b) => <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>)}
                            </select>
                            <SelectIcon loading={bankListLoading} />
                        </div>
                    </div>
                    <div>
                        <Label required>Mode of Pay</Label>
                        <div className="relative">
                            <select className={`${inputCls} appearance-none pr-10`} value={modeOfPay} onChange={(e) => setModeOfPay(e.target.value)} disabled={saveLoading}>
                                {MODE_OPTIONS.map((m) => <option key={m.Mode} value={m.Mode}>{m.Mode_Desc}</option>)}
                            </select>
                            <SelectIcon loading={false} />
                        </div>
                    </div>
                    <div><Label required>Payment Number</Label><input className={inputCls} value={paymentNo} onChange={(e) => setPaymentNo(e.target.value)} disabled={saveLoading} /></div>
                    <div><Label required>Payment Date</Label><CustomDatePicker value={paymentDate} onChange={setPaymentDate} disabled={saveLoading} maxDate={new Date()} /></div>
                    <div><Label>Payment Amount</Label><input className={inputCls} value={paymentAmount ? paymentAmount.toFixed(2) : ''} disabled readOnly /></div>
                    <div className="md:col-span-3">
                        <Label required>Remarks</Label>
                        <textarea rows={2} className={`${inputCls} resize-none`} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={saveLoading} />
                    </div>
                </div>
            </Card>

            <div className="flex items-center justify-end gap-3 pb-2">
                <button onClick={handleReset} disabled={saveLoading} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                    <RotateCcw className="h-4 w-4" /> Reset
                </button>
                <button onClick={handleSubmit} disabled={saveLoading} className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                    {saveLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><Send className="h-4 w-4" /> Submit</>}
                </button>
            </div>
        </>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const FDPayments = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const currentUser = userData?.userName || userData?.userId || userData?.UID || '';

    const {
        fdPaymentTypes, fdPaymentTypesLoading, bankList, bankListLoading,
        fdNumbers, fdNumbersLoading, dcaList, dcaListLoading, subDcaByDca, subDcaLoadingByDca,
    } = useSelector(selectFDPayments);

    const [transTypeId, setTransTypeId] = useState('');

    useEffect(() => {
        dispatch(fetchFDPaymentTypes());
        dispatch(fetchFDBankList());
        dispatch(fetchFDNumbers());
        dispatch(fetchFDDCAList());
        return () => { dispatch(resetFDPayments()); };
    }, [dispatch]);

    const onLoadSubDca = (code) => dispatch(fetchFDSubDCAByDCACode(code));

    const sharedLookups = { currentUser, roleId, bankList, bankListLoading, fdNumbers, fdNumbersLoading };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            {/* Hero header */}
            <div className="px-4 md:px-6 pt-6">
                <div className="max-w-7xl mx-auto">
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 md:p-8 text-white">
                        <div className="absolute inset-0 opacity-10"
                            style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                        <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                    <Landmark className="h-7 w-7 text-white" />
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Accounts Module</span>
                                    <h1 className="text-2xl md:text-3xl font-black tracking-tight">{menuData?.name || 'Fixed Deposit'}</h1>
                                    <p className="text-indigo-200 text-sm mt-0.5">Open, close, partially close or record interest on fixed deposits</p>
                                </div>
                            </div>
                            <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                                <div className="text-right">
                                    <p className="text-xs uppercase tracking-wider">Module</p>
                                    <p className="text-sm font-bold text-white">Accounts / FD Payments</p>
                                </div>
                                <Navigation className="h-5 w-5 opacity-60" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
                <Card>
                    <CardHeader icon={Landmark} title="Transaction Type" subtitle="Choose what you want to record" />
                    <div className="p-6 md:p-8 max-w-sm">
                        <Label required>Transaction Type</Label>
                        <div className="relative">
                            <select className={`${inputCls} appearance-none pr-10`} value={transTypeId} onChange={(e) => setTransTypeId(e.target.value)} disabled={fdPaymentTypesLoading}>
                                <option value="">-Please Select-</option>
                                {fdPaymentTypes.map((t) => <option key={t.FDTran_id} value={t.FDTran_id}>{t.FDTran_name}</option>)}
                            </select>
                            <SelectIcon loading={fdPaymentTypesLoading} />
                        </div>
                    </div>
                </Card>

                {transTypeId === '1' && <OpenFDSection {...sharedLookups} />}
                {transTypeId === '2' && <ClosePartialFDSection mode="Close" {...sharedLookups} dcaList={dcaList} dcaListLoading={dcaListLoading} subDcaByDca={subDcaByDca} subDcaLoadingByDca={subDcaLoadingByDca} onLoadSubDca={onLoadSubDca} />}
                {transTypeId === '3' && <ClosePartialFDSection mode="Partial" {...sharedLookups} dcaList={dcaList} dcaListLoading={dcaListLoading} subDcaByDca={subDcaByDca} subDcaLoadingByDca={subDcaLoadingByDca} onLoadSubDca={onLoadSubDca} />}
                {transTypeId === '4' && <InterestSection {...sharedLookups} />}
            </div>
        </div>
    );
};

export default FDPayments;
